import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Tag, Button, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import { STATUS_COLORS } from '../../_shared/constants';
import { minutesToHHMM } from '../../_shared/utils';
import {
  ReportSortButton,
  ReportSearchFilter,
  HeaderTitle,
} from '../../reports/_components/ReportHeaderComponents';

export function getApprovalColumns({
  searchParams,
  updateParams,
  t,
  canApprove,
  statusFilter,
  canApproveEntry,
  handleApprove,
  onReject,
}) {
  const cols = [
    canApprove && {
      title: () => (
        <HeaderTitle title={t('employee')}>
          <ReportSearchFilter
            field="employeeName"
            title={t('employee')}
            searchParams={searchParams}
            updateParams={updateParams}
          />
          <ReportSortButton
            field="employee"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      key: 'employee',
      render: (_, r) =>
        `${r.employee?.firstName || ''} ${r.employee?.lastName || ''}`.trim() ||
        '-',
    },
    {
      title: () => (
        <HeaderTitle title={t('date')}>
          <ReportSortButton
            field="date"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'date',
      key: 'date',
      render: (d) => dayjs(d).format('DD.MM.YYYY'),
    },
    {
      title: () => (
        <HeaderTitle title={t('project')}>
          <ReportSearchFilter
            field="projectName"
            title={t('project')}
            searchParams={searchParams}
            updateParams={updateParams}
          />
          <ReportSortButton
            field="project"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'project',
      key: 'project',
      render: (project, record) => {
        if (!project) {
          return (
            <span className="italic text-gray-400">
              {record?.autoGenerated ? t('unallocatedTime') : t('noProject')}
            </span>
          );
        }
        return (
          <span className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: project.color || '#1890ff' }}
            />
            {project.name || '-'}
          </span>
        );
      },
    },
    {
      title: () => (
        <HeaderTitle title={t('duration')}>
          <ReportSortButton
            field="duration"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'duration',
      key: 'duration',
      render: (d) => <span className="font-mono">{minutesToHHMM(d)}</span>,
    },
    canApprove && {
      title: t('billable'),
      dataIndex: 'billable',
      key: 'billable',
      render: (v) => (v ? <Tag color="green">$</Tag> : <Tag>-</Tag>),
    },
    {
      title: () => (
        <HeaderTitle title={t('status', { ns: 'tables' })}>
          <ReportSortButton
            field="status"
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={STATUS_COLORS[s]}>{t(s)}</Tag>,
    },
    {
      title: () => (
        <HeaderTitle title={t('description')}>
          <ReportSearchFilter
            field="description"
            title={t('description')}
            searchParams={searchParams}
            updateParams={updateParams}
          />
        </HeaderTitle>
      ),
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (d) => (
        <div className="max-w-[200px] truncate" title={d}>
          {d || '-'}
        </div>
      ),
    },
    ...(canApprove && statusFilter === 'SUBMITTED'
      ? [
          {
            title: t('actions', { ns: 'tables' }),
            key: 'actions',
            fixed: 'right',
            render: (_, record) =>
              canApproveEntry(record) ? (
                <div
                  className="flex items-center justify-end gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Popconfirm
                    title={t('approveEntry')}
                    onConfirm={() => handleApprove([record._id])}
                    okText={t('yes')}
                    cancelText={t('no')}
                  >
                    <Button
                      type="default"
                      className="btn-default-action h-7 w-7 shrink-0 !border-green-600 !p-0 !text-green-600 hover:!border-green-600 hover:!bg-green-50"
                      icon={<CheckOutlined style={{ fontSize: 12 }} />}
                    />
                  </Popconfirm>
                  <Button
                    type="default"
                    className="btn-default-action h-7 w-7 shrink-0 !border-red-600 !p-0 !text-red-600 hover:!border-red-600 hover:!bg-red-50"
                    icon={<CloseOutlined style={{ fontSize: 12 }} />}
                    onClick={() => onReject(record._id)}
                  />
                </div>
              ) : null,
          },
        ]
      : []),
  ];

  return cols.filter(Boolean);
}
